import { escapeHTML, shortValue, showStatus } from "./ui-utils";

type API = <T>(path: string, options?: RequestInit) => Promise<T>;

type ConnectorDefinition = {
  key: string;
  name: string;
  version: string;
  custom: boolean;
  spec: { inbound: { routingKeys: string[] } };
};

type ConnectorBinding = {
  id: string;
  definitionKey: string;
  definitionName: string;
  definitionVersion: string;
  name: string;
  environment: string;
  status: string;
  callbackUrl: string;
  productId: string;
  productName: string;
};

type Product = { id: string; name: string; productType: string };
type Subscription = { id: string; productId: string; productName: string; status: string; notificationEmail?: string };
type SubscriptionIntegration = {
  id: string;
  subscriptionId: string;
  bindingId: string;
  externalRefs: Record<string, string>;
};
type IntegrationMessage = {
  id: string;
  bindingId: string;
  subscriptionId?: string;
  externalMessageId: string;
  routingStatus: string;
  deliveryStatus: string;
  createdAt: string;
};
type ConnectorRequestAttempt = {
  id: string;
  bindingId: string;
  integrationMessageId?: string;
  method: string;
  requestUri: string;
  protocol: string;
  host: string;
  remoteAddress: string;
  headers: Record<string, string[]>;
  rawBody?: string;
  rawBodyBase64: string;
  bodyHash: string;
  bodySize: number;
  outcome: string;
  httpStatus: number;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
};

function requestHeader(attempt: ConnectorRequestAttempt, name: string): string {
  const entry = Object.entries(attempt.headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  return entry?.[1]?.join(", ") || "";
}

function displayRequestBody(attempt: ConnectorRequestAttempt): string {
  if (attempt.rawBody === undefined) return `[binary body · Base64]\n${attempt.rawBodyBase64}`;
  try {
    return JSON.stringify(JSON.parse(attempt.rawBody), null, 2);
  } catch {
    return attempt.rawBody;
  }
}

export function initializeConnectorConsole(api: API): { load: () => Promise<void> } {
  let definitions: ConnectorDefinition[] = [];
  let bindings: ConnectorBinding[] = [];
  let products: Product[] = [];
  let subscriptions: Subscription[] = [];
  let integrations: SubscriptionIntegration[] = [];
  let messages: IntegrationMessage[] = [];
  let attempts: ConnectorRequestAttempt[] = [];

  function selectedBinding(): ConnectorBinding | undefined {
    return bindings.find(item => item.id === document.querySelector<HTMLSelectElement>("#connector-route-binding")!.value);
  }

  function renderRouteFields(): void {
    const binding = selectedBinding();
    const definition = definitions.find(item => item.key === binding?.definitionKey);
    const keys = definition?.spec.inbound.routingKeys || [];
    const references = Object.fromEntries(keys.map(key => [key, ""]));
    document.querySelector<HTMLTextAreaElement>("#connector-external-refs")!.value = JSON.stringify(references, null, 2);
    const eligible = subscriptions.filter(item => item.productId === binding?.productId);
    document.querySelector<HTMLSelectElement>("#connector-route-subscription")!.innerHTML = eligible.map(item =>
      `<option value="${escapeHTML(item.id)}">订阅 ${escapeHTML(shortValue(item.id))} · ${escapeHTML(item.status)}</option>`,
    ).join("");
    document.querySelector<HTMLButtonElement>("#save-connector-route")!.disabled = !binding?.productId || eligible.length === 0;
    renderNotificationEmail();
  }

  function renderNotificationEmail(): void {
    const selected = subscriptions.find(item => item.id === document.querySelector<HTMLSelectElement>("#connector-route-subscription")!.value);
    document.querySelector<HTMLInputElement>("#connector-notification-email")!.value = selected?.notificationEmail || "";
  }

  function render(): void {
    document.querySelector<HTMLSelectElement>("#connector-definition")!.innerHTML = definitions.map((item, index) =>
      `<option value="${escapeHTML(item.key)}">${escapeHTML(item.custom ? item.name : `系统协议 ${index + 1}`)} · ${escapeHTML(item.version)}</option>`,
    ).join("");
    document.querySelector<HTMLSelectElement>("#connector-product")!.innerHTML = `<option value="">暂不绑定 · 只接收并保存消息</option>` + products
      .filter(item => item.productType === "NOTIFICATION")
      .map(item => `<option value="${escapeHTML(item.id)}">通知产品 · ${escapeHTML(shortValue(item.id, 14))}</option>`).join("");
    document.querySelector<HTMLSelectElement>("#connector-route-binding")!.innerHTML = bindings.map(item =>
      `<option value="${escapeHTML(item.id)}">${escapeHTML(item.name)} · ${escapeHTML(shortValue(item.id))}</option>`,
    ).join("");
    document.querySelector<HTMLSelectElement>("#connector-credential-binding")!.innerHTML = bindings.map(item =>
      `<option value="${escapeHTML(item.id)}">${escapeHTML(item.name)} · ${escapeHTML(shortValue(item.id))}</option>`,
    ).join("");
    document.querySelector<HTMLElement>("#connector-binding-rows")!.innerHTML = bindings.map(item => `
      <tr>
        <td><strong>${escapeHTML(item.name)}</strong><small>协议 ${escapeHTML(item.definitionVersion)} · ${escapeHTML(shortValue(item.id))}</small></td>
        <td><span class="status-chip">${escapeHTML(item.environment)}</span></td>
        <td><code title="${escapeHTML(item.callbackUrl)}">${escapeHTML(shortValue(item.callbackUrl, 18))}</code></td>
        <td><span class="status-chip ${escapeHTML(item.status.toLowerCase())}">${escapeHTML(item.status)}</span></td>
        <td class="row-actions"><button data-copy-url="${escapeHTML(item.callbackUrl)}" type="button">复制 URL</button><button data-rotate-binding="${escapeHTML(item.id)}" type="button">轮换密钥</button></td>
      </tr>
    `).join("");
    document.querySelector<HTMLElement>("#connector-binding-empty")!.hidden = bindings.length !== 0;
    document.querySelector<HTMLElement>("#connector-integration-rows")!.innerHTML = integrations.map(item => `
      <tr><td><strong>${escapeHTML(shortValue(item.subscriptionId))}</strong><small>${escapeHTML(item.subscriptionId)}</small></td><td>${escapeHTML(bindings.find(binding => binding.id === item.bindingId)?.name || item.bindingId)}</td><td><code>${escapeHTML(JSON.stringify(item.externalRefs))}</code></td><td>${escapeHTML(subscriptions.find(subscription => subscription.id === item.subscriptionId)?.notificationEmail || "—")}</td></tr>
    `).join("");
    document.querySelector<HTMLElement>("#connector-integration-empty")!.hidden = integrations.length !== 0;
    document.querySelector<HTMLElement>("#connector-message-rows")!.innerHTML = messages.map(item => `
      <tr><td><strong>${escapeHTML(shortValue(item.externalMessageId, 12))}</strong><small>${escapeHTML(new Date(item.createdAt).toLocaleString("zh-CN"))}</small></td><td>${escapeHTML(bindings.find(binding => binding.id === item.bindingId)?.name || shortValue(item.bindingId))}</td><td><span class="status-chip">${escapeHTML(item.routingStatus)}</span></td><td><span class="status-chip ${escapeHTML(item.deliveryStatus.toLowerCase())}">${escapeHTML(item.deliveryStatus)}</span></td></tr>
    `).join("");
    document.querySelector<HTMLElement>("#connector-message-empty")!.hidden = messages.length !== 0;
    document.querySelector<HTMLElement>("#connector-attempt-rows")!.innerHTML = attempts.map(item => {
      const source = requestHeader(item, "x-forwarded-for").split(",")[0]?.trim() || item.remoteAddress;
      const result = item.errorCode || (item.httpStatus ? `HTTP ${item.httpStatus}` : item.outcome);
      return `
        <tr>
          <td><strong>${escapeHTML(new Date(item.createdAt).toLocaleString("zh-CN"))}</strong><small>${escapeHTML(shortValue(item.id, 14))}</small></td>
          <td>${escapeHTML(bindings.find(binding => binding.id === item.bindingId)?.name || shortValue(item.bindingId))}<small>${escapeHTML(item.method)} · ${escapeHTML(source)}</small></td>
          <td><span class="status-chip ${escapeHTML(item.outcome.toLowerCase())}">${escapeHTML(item.outcome)}</span><small>${escapeHTML(result)}</small></td>
          <td><code>${escapeHTML(shortValue(item.bodyHash, 14))}</code><small>${escapeHTML(String(item.bodySize))} bytes</small></td>
          <td class="row-actions"><button data-attempt-id="${escapeHTML(item.id)}" type="button">查看完整请求</button></td>
        </tr>`;
    }).join("");
    document.querySelector<HTMLElement>("#connector-attempt-empty")!.hidden = attempts.length !== 0;
    document.querySelector<HTMLButtonElement>("#create-connector-binding")!.disabled = definitions.length === 0;
    document.querySelector<HTMLButtonElement>("#rotate-connector-secret")!.disabled = bindings.length === 0;
    renderRouteFields();
  }

  document.querySelector<HTMLFormElement>("#connector-definition-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    const splitValues = (selector: string): string[] => document.querySelector<HTMLInputElement>(selector)!.value
      .split(",").map(value => value.trim()).filter(Boolean);
    try {
      const body = await api<{ definition: ConnectorDefinition }>("/v1/provider/connector-definitions", {
        method: "POST",
        body: JSON.stringify({
          name: document.querySelector<HTMLInputElement>("#connector-definition-name")!.value.trim(),
          event: document.querySelector<HTMLInputElement>("#connector-event")!.value.trim(),
          dataPointer: document.querySelector<HTMLInputElement>("#connector-data-pointer")!.value.trim(),
          signatureHeader: document.querySelector<HTMLInputElement>("#connector-signature-header")!.value.trim(),
          messageIdPointers: splitValues("#connector-message-id-pointers"),
          routingKeys: splitValues("#connector-routing-keys"),
          acknowledgementBody: document.querySelector<HTMLInputElement>("#connector-acknowledgement")!.value.trim(),
        }),
      });
      showStatus("connector-definition-status", `协议已保存：${body.definition.name}`);
      await load();
      document.querySelector<HTMLSelectElement>("#connector-definition")!.value = body.definition.key;
    } catch (error) {
      showStatus("connector-definition-status", error instanceof Error ? error.message : "协议定义创建失败", true);
    }
  });

  async function load(): Promise<void> {
    try {
      const [definitionBody, bindingBody, productBody, subscriptionBody, integrationBody, messageBody] = await Promise.all([
        api<{ definitions: ConnectorDefinition[] }>("/v1/provider/connector-definitions"),
        api<{ bindings: ConnectorBinding[] }>("/v1/provider/connector-bindings"),
        api<{ products: Product[] }>("/v1/provider/products"),
        api<{ subscriptions: Subscription[] }>("/v1/provider/notification-subscriptions"),
        api<{ integrations: SubscriptionIntegration[] }>("/v1/provider/subscription-integrations"),
        api<{ messages: IntegrationMessage[]; attempts: ConnectorRequestAttempt[] }>("/v1/provider/connector-messages?limit=100"),
      ]);
      definitions = definitionBody.definitions;
      bindings = bindingBody.bindings;
      products = productBody.products;
      subscriptions = subscriptionBody.subscriptions;
      integrations = integrationBody.integrations;
      messages = messageBody.messages;
      attempts = messageBody.attempts || [];
      document.querySelector("#summary-count")!.textContent = `${bindings.length} CONNECTORS`;
      render();
    } catch (error) {
      showStatus("connector-load-status", error instanceof Error ? error.message : "Connector 加载失败", true);
    }
  }

  document.querySelector<HTMLFormElement>("#connector-binding-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      const body = await api<{ binding: ConnectorBinding }>("/v1/provider/connector-bindings", {
        method: "POST",
        body: JSON.stringify({
          definitionKey: document.querySelector<HTMLSelectElement>("#connector-definition")!.value,
          productId: document.querySelector<HTMLSelectElement>("#connector-product")!.value,
          name: document.querySelector<HTMLInputElement>("#connector-name")!.value.trim(),
          environment: document.querySelector<HTMLSelectElement>("#connector-environment")!.value,
          signingSecret: document.querySelector<HTMLInputElement>("#connector-signing-secret")!.value,
        }),
      });
      document.querySelector<HTMLInputElement>("#connector-signing-secret")!.value = "";
      showStatus("connector-binding-status", `Connector 已创建：${body.binding.callbackUrl}`);
      await load();
    } catch (error) {
      showStatus("connector-binding-status", error instanceof Error ? error.message : "Connector 创建失败", true);
    }
  });

  document.querySelector<HTMLFormElement>("#connector-route-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      const subscriptionId = document.querySelector<HTMLSelectElement>("#connector-route-subscription")!.value;
      const externalRefs = JSON.parse(document.querySelector<HTMLTextAreaElement>("#connector-external-refs")!.value) as unknown;
      if (!externalRefs || typeof externalRefs !== "object" || Array.isArray(externalRefs)) {
        throw new Error("External refs 必须是 JSON Object");
      }
      await api(`/v1/provider/notification-subscriptions/${subscriptionId}/connector`, {
        method: "PUT",
        body: JSON.stringify({
          bindingId: document.querySelector<HTMLSelectElement>("#connector-route-binding")!.value,
          externalRefs,
          notificationEmail: document.querySelector<HTMLInputElement>("#connector-notification-email")!.value.trim(),
        }),
      });
      showStatus("connector-route-status", "订阅路由已保存；匹配的回调将自动幂等记账。");
      await load();
    } catch (error) {
      showStatus("connector-route-status", error instanceof Error ? error.message : "订阅路由保存失败", true);
    }
  });

  document.querySelector<HTMLFormElement>("#connector-credential-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    const bindingID = document.querySelector<HTMLSelectElement>("#connector-credential-binding")!.value;
    const signingSecret = document.querySelector<HTMLInputElement>("#connector-rotated-secret")!.value;
    try {
      await api(`/v1/provider/connector-bindings/${encodeURIComponent(bindingID)}`, {
        method: "PUT",
        body: JSON.stringify({ signingSecret }),
      });
      document.querySelector<HTMLInputElement>("#connector-rotated-secret")!.value = "";
      showStatus("connector-credential-status", "签名密钥已轮换；Callback URL 保持不变，后续请求立即使用新密钥验签。");
      await load();
    } catch (error) {
      showStatus("connector-credential-status", error instanceof Error ? error.message : "签名密钥轮换失败", true);
    }
  });

  document.querySelector<HTMLSelectElement>("#connector-route-binding")!.addEventListener("change", renderRouteFields);
  document.querySelector<HTMLSelectElement>("#connector-route-subscription")!.addEventListener("change", renderNotificationEmail);
  document.querySelector<HTMLElement>("#connector-binding-rows")!.addEventListener("click", async event => {
    const rotateButton = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-rotate-binding]");
    if (rotateButton) {
      document.querySelector<HTMLSelectElement>("#connector-credential-binding")!.value = rotateButton.dataset.rotateBinding || "";
      document.querySelector<HTMLInputElement>("#connector-rotated-secret")!.focus();
      document.querySelector<HTMLElement>("#connector-credential-form")!.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-copy-url]");
    if (!button) return;
    try {
      await navigator.clipboard.writeText(button.dataset.copyUrl || "");
      button.textContent = "已复制";
    } catch {
      showStatus("connector-load-status", "浏览器未允许复制，请从表格中手动复制 URL。", true);
    }
  });

  document.querySelector<HTMLElement>("#connector-attempt-rows")!.addEventListener("click", event => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-attempt-id]");
    const attempt = attempts.find(item => item.id === button?.dataset.attemptId);
    if (!attempt) return;
    document.querySelector<HTMLElement>("#connector-attempt-title")!.textContent = `${attempt.method} · ${attempt.outcome} · HTTP ${attempt.httpStatus}`;
    document.querySelector<HTMLElement>("#connector-attempt-meta")!.textContent = `${attempt.id} · ${new Date(attempt.createdAt).toLocaleString("zh-CN")} · SHA-256 ${attempt.bodyHash}`;
    document.querySelector<HTMLElement>("#connector-attempt-headers")!.textContent = JSON.stringify(attempt.headers, null, 2);
    document.querySelector<HTMLElement>("#connector-attempt-body")!.textContent = displayRequestBody(attempt);
    document.querySelector<HTMLDialogElement>("#connector-attempt-dialog")!.showModal();
  });
  document.querySelector<HTMLButtonElement>("#close-connector-attempt")!.addEventListener("click", () => {
    document.querySelector<HTMLDialogElement>("#connector-attempt-dialog")!.close();
  });

  return { load };
}
